import {HttpClient, HttpContext, HttpParams} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {BehaviorSubject, Observable, ReplaySubject, map, of, catchError, Subscription} from 'rxjs';
import {Exam, ExamForm} from './exam.model';
import { APIResponse } from 'src/app/shared/data-access/api-response.model';
import { environment } from 'src/environments/environment';
import {AuthService} from "../../login/data-access/auth.service";
import {catchApiError} from "../../shared/utils/api-error";
import {encodeParam} from "../../shared/utils/query-string";
import {SKIP_NANOBAR} from "../../shared/ui/nanobar/nanobar.interceptor";

@Injectable({
  providedIn: 'root',
})
export class ExamService {
  httpClient: HttpClient = inject(HttpClient);

  authService = inject(AuthService);
  user_id: string = ''

  userSub: Subscription = this.authService.user$.subscribe((user) => {
    if (user) {
      this.user_id = user.netid
    }
  });

  fetchExam(uuid: string): Observable<Exam> {
    // const apiUrl = `${environment.apiUrl}/exam/details`;
    // const params = new HttpParams().set('uuid', uuid);
    // this.httpClient.get<APIResponse<Exam>>(apiUrl + params).subscribe((response) => {
    //   if (response.success) {
    //     this.currentExam$.next(response.data);
    //   }

    // Dummy return
    return of({} as Exam);
  }

  fetchCourseExams(course_id: number): Observable<Exam[]> {
    const apiUrl = `${environment.baseUrl}/exam/listforcourse`;
    const params = new HttpParams().set('course_id', course_id.toString());

    return this.httpClient.get<APIResponse>(apiUrl + params).pipe(
      map((response) => {
        if (!response.success) {
          return [];
        }
        return response.result;
      })
    );
  }

  getExamDetailsType(exam_id: number): Observable<APIResponse> {
    const apiUrl = `${environment.baseUrl}/exam/details/type?exam_id=${encodeParam(exam_id)}`;

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    );
  }

  saveExam(exam: string): Observable<APIResponse> {
    const tempObj: ExamForm = JSON.parse(exam)
    if(tempObj.construct.type == null) {
      tempObj.construct.type = 'exam'
    }
    const examObjJSON = JSON.stringify({
      exam_title: tempObj.construct.exam_title,
      type: tempObj.construct.type,
      exam_id: tempObj.construct.exam_id,
      course_id: tempObj.construct.course_id,
      open: false,
      active: false,
      graded: true,
      attempts_max: tempObj.construct.attemptsMax,
      parts: tempObj.construct.general.parts,
      flows: tempObj.flows,
      timeframes: tempObj.assign.timeframes,
      reports: tempObj.reports
    })
    let body = new HttpParams()
      .set('exam_title', tempObj.construct.exam_title)
      .set('type', tempObj.construct.type)
      .set('exam_id', tempObj.construct.exam_id)
      .set('course_id', tempObj.construct.course_id)
      .set('open', false)
      .set('active', false)
      .set('graded', true)
      .set('late_start', Number(tempObj.construct.late_start))
      .set('late_finish', Number(tempObj.construct.late_finish))
      .set('attempts_max', tempObj.construct.attemptsMax)
      .set('reports', JSON.stringify(tempObj.reports));

    for(let part of tempObj.construct.general.parts) {
      body = body.append('parts', JSON.stringify(part))
    }

    for(let timeframe of tempObj.assign.timeframes) {
      if(timeframe.first_start != null)
        timeframe.first_start = new Date(timeframe.first_start)
      if(timeframe.last_start != null)
        timeframe.last_start = new Date(timeframe.last_start)
      if(timeframe.finish_by != null)
        timeframe.finish_by = new Date(timeframe.finish_by)
      body = body.append('timeframes', JSON.stringify(timeframe))
    }

    // TODO fix exam flow on the form, exampart_next, exampart_previous etc.
    for(let flow of tempObj.flows) {
      body = body.append('flows', JSON.stringify(flow))
    }

    const apiUrl = `${environment.nodeUrl}/exam/save/node`;
    //let body = examObjJSON

    return this.httpClient.post<APIResponse>(apiUrl, body).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  deleteExam(exam_id: number): Observable<APIResponse> {
    const apiUrl = `${environment.baseUrl}/exam/delete?exam_id=${encodeParam(exam_id)}`;

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    );
  }

  listAdminExam(): Observable<APIResponse> {
    const apiUrl = `${environment.nodeUrl}/exam/listadmin`
    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  listAllExams(page: number, take: number, search: string, active: boolean, open: boolean): Observable<APIResponse> {
    const apiUrl = `${environment.nodeUrl}/exam/listall?page=${encodeParam(page)}&take=${encodeParam(take)}&search=${encodeParam(search)}&active=${encodeParam(active)}&open=${encodeParam(open)}`;
    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  listMyExams(page: number, take: number, search: string, active: boolean, open: boolean): Observable<APIResponse> {
    const apiUrl = `${environment.nodeUrl}/exam/listmine?page=${encodeParam(page)}&take=${encodeParam(take)}&search=${encodeParam(search)}&active=${encodeParam(active)}&open=${encodeParam(open)}`;
    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  listClassroomGrades(page: number, take: number, search: string): Observable<APIResponse> {
    const apiUrl = `${environment.baseUrl}/exam/student/classroom/grades?page=${encodeParam(page)}&take=${encodeParam(take)}&search=${encodeParam(search)}`;
    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  examDetails(id: number, clone?: boolean): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/details?exam_id=${encodeParam(id)}`
    if(clone)
      apiUrl += `&clone=${encodeParam(clone)}`
    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  changeExamOwner(exam_id: number, user_id: number, force?: boolean): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/chown?exam_id=${encodeParam(exam_id)}&user_id=${encodeParam(user_id)}`
    if(force)
      apiUrl = `${environment.baseUrl}/exam/chown/force?exam_id=${encodeParam(exam_id)}&user_id=${encodeParam(user_id)}`

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  listActiveExams(active: boolean, open: boolean): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/listactiveopen?active=${encodeParam(active)}&open=${encodeParam(open)}`

    // TODO add optionals to have it be non active and non open exams as well on frontend
    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  previewExam(exam_id: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/preview/flat?exam_id=${encodeParam(exam_id)}`

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  startExam(exam_id: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/student/take?exam_id=${encodeParam(exam_id)}`

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  submitPart(exam_uuid: string, part_position: number): Observable<APIResponse> {
    // TODO check how to get uuid from take
    let apiUrl = `${environment.baseUrl}/exam/part/finish?exam_uuid=${encodeParam(exam_uuid)}&part_position=${encodeParam(part_position)}`

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  recordAnswer(exam_uuid: string, part_position: number, question_position: number, value?: string): Observable<APIResponse> {
    // TODO add the question value
    let apiUrl = `${environment.baseUrl}/exam/answer?exam_uuid=${encodeParam(exam_uuid)}&part_position=${encodeParam(part_position)}&question_position=${encodeParam(question_position)}`
    let body = new HttpParams()
    if(value != null)
      body = new HttpParams().set('value', value)

    return this.httpClient.post<APIResponse>(apiUrl, body, { context: new HttpContext().set(SKIP_NANOBAR, true) }).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  recordAnswerText(exam_uuid: string, part_position: number, question_position: number, value?: string): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/answer/text?exam_uuid=${encodeParam(exam_uuid)}&part_position=${encodeParam(part_position)}&question_position=${encodeParam(question_position)}`
    let body = new HttpParams()
    if(value != null)
      body = new HttpParams().set('value', value)

    return this.httpClient.put<APIResponse>(apiUrl, body, { context: new HttpContext().set(SKIP_NANOBAR, true) }).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  answerUpload(file: any, exam_uuid: string, part_position: string, question_position: string): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/instance/answerupload`
    const formData = new FormData();
    if (file instanceof File) {
      formData.append('file', file, file.name);
    } else {
      formData.append('file', file, `recording_part${part_position}_q${question_position}.webm`);
    }
    formData.append('exam_uuid', exam_uuid);
    formData.append('part_position', part_position);
    formData.append('question_position', question_position);

    return this.httpClient.post<APIResponse>(apiUrl, formData).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }


  runCodeSample(exam_uuid: string, part_position: number, question_position: number, code?: string): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/codingsample?exam_uuid=${encodeParam(exam_uuid)}&part_position=${encodeParam(part_position)}&question_position=${encodeParam(question_position)}`
    let body = new HttpParams()
    if(code != null)
      body = new HttpParams().set('code', code)

    return this.httpClient.post<APIResponse>(apiUrl, body).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  // TODO create API response
  continueExam(uuid: string) {
    // TODO maybe do some auth guards on our end to check if this should be called
    let apiUrl = `${environment.baseUrl}/exam/student/continue/details?uuid=${encodeParam(uuid)}&include_questions=true`

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  previewTake(exam_id: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/preview/take?exam_id=${encodeParam(exam_id)}`

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  addGrader(exam_id: number, user_ids: number[], exam_uuid?: number ): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/graders/assign?exam_id=${encodeParam(exam_id)}&add_user_id=${encodeParam(user_ids)}`
    if(exam_uuid != null) {
      apiUrl += `&exam_uuid=${encodeParam(exam_uuid)}`
    }

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  removeGrader(exam_id: number, user_ids: number[], exam_uuid?: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/graders/assign?exam_id=${encodeParam(exam_id)}&remove_user_id=${encodeParam(user_ids)}`
    if(exam_uuid != null) {
      apiUrl += `&exam_uuid=${encodeParam(exam_uuid)}`
    }

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  listGraders(exam_id: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/graders/list?exam_id=${encodeParam(exam_id)}`

    return this.httpClient.get<APIResponse>(apiUrl, ).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  searchForExamGraders(course_id: number, search: string): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/course/users/listexamgraders?course_id=${encodeParam(course_id)}&search=${encodeParam(search)}`

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  getMyExamInstances(course_id: number, limit?: number, page?: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/instructor/tograde?course_id=${encodeParam(course_id)}`
    if(limit != null){
      apiUrl += `&limit=${encodeParam(limit)}`
    }
    if(page != null) {
      apiUrl += `&page=${encodeParam(page)}`
    }

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  reviewExamInstance(exam_uuid: string, isStudent?: boolean): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/instructor/review?exam_uuid=${encodeParam(exam_uuid)}`
    if(isStudent) {
      apiUrl = `${environment.baseUrl}/exam/student/review?exam_uuid=${encodeParam(exam_uuid)}`
    }

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  getAllExamInstances(exam_id: number, limit: number, page: number, mode: string, search: string): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/instance/search?exam_id=${encodeParam(exam_id)}&limit=${encodeParam(limit)}&page=${encodeParam(page)}`
    if(mode != ''){
      apiUrl += `&modes=${encodeParam(mode)}`
    }

    if (search != '') {
      apiUrl += `&name=${encodeParam(search)}`
    }

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  extendExamInstance(exam_uuid: string, continue_minutes: number, part_position: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/instance/extend?exam_uuid=${encodeParam(exam_uuid)}&continue_minutes=${encodeParam(continue_minutes)}&part_position=${encodeParam(part_position)}`

    return this.httpClient.put<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  newOverallDueDate(exam_uuid: string, overall_due: string): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/instance/extend?exam_uuid=${encodeParam(exam_uuid)}&overall_due=${encodeParam(overall_due)}`

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  recordExamInstanceComment(exam_uuid: string, comment: string): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/instance/comment?exam_uuid=${encodeParam(exam_uuid)}`
    const body = new HttpParams().set('comment', comment)

    return this.httpClient.post<APIResponse>(apiUrl, body).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  overwriteSingleQuestionCorrectness(exam_uuid: string, part_position: number, question_position: number, correctness_manual: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/instance/gradequestion?exam_uuid=${encodeParam(exam_uuid)}&part_position=${encodeParam(part_position)}&question_position=${encodeParam(question_position)}&correctness_manual=${encodeParam(correctness_manual)}`

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  overwritePartScore(exam_uuid: string, part_position: number, part_score_override: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/instance/gradepart?exam_uuid=${encodeParam(exam_uuid)}&part_position=${encodeParam(part_position)}&part_score_override=${encodeParam(part_score_override)}`

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  examPartialReset(exam_uuid: string, part_position: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/instance/partialreset?exam_uuid=${encodeParam(exam_uuid)}&part_position=${encodeParam(part_position)}`

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  examFullReset(exam_uuid: string): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/instance/reset?exam_uuid=${encodeParam(exam_uuid)}`

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  assignNewExamInstance(exam_id: number, username: string, mode: string | null, due_override: Date | null): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/assign?exam_id=${encodeParam(exam_id)}&username=${encodeParam(username)}`
    if(mode != null)
      apiUrl += `&mode=${encodeParam(mode)}`
    if(due_override != null)
      apiUrl += `&due_override=${encodeParam(due_override)}`

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  exportReportFields(exam_id: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/report/fields?exam_id=${encodeParam(exam_id)}`

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  exportReportSave(json: string): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/report/save`
    const body = new HttpParams().set('json', json)

    return this.httpClient.post<APIResponse>(apiUrl, body).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  exportReportList(exam_id: number, limit: number, page: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/report/list?exam_id=${encodeParam(exam_id)}`

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  exportReportDetails(report_id: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/report/details?report_id=${encodeParam(report_id)}`

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  exportReportRun(report_id: number, is_fully_complete: boolean): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/report/run?report_id=${encodeParam(report_id)}&is_fully_complete=${encodeParam(is_fully_complete)}`

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  exportConfigDelete(report_id: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/report/retire?report_id=${encodeParam(report_id)}`

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  activateExam(exam_id: number, active: boolean): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/active?exam_id=${encodeParam(exam_id)}&active=${encodeParam(active)}`

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  listSubsections(course_id: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/course/subsections/list?course_id=${encodeParam(course_id)}`

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  openExam(exam_id: number, open: boolean): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/open?exam_id=${encodeParam(exam_id)}&open=${encodeParam(open)}`

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  listExamEditors(exam_id: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/editors/list?exam_id=${encodeParam(exam_id)}`

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  addExamEditor(exam_id: number, user_id: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/editors/edit?exam_id=${encodeParam(exam_id)}&add=${encodeParam(user_id)}`

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  removeExamEditor(exam_id: number, user_id: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/editors/edit?exam_id=${encodeParam(exam_id)}&remove=${encodeParam(user_id)}`

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  downloadAnswer(exam_uuid: string, part_position: number, question_position: number): Observable<Blob> {
    let apiUrl = `${environment.baseUrl}/exam/instructor/answerdownload?exam_uuid=${encodeParam(exam_uuid)}&part_position=${encodeParam(part_position)}&question_position=${encodeParam(question_position)}`

    return this.httpClient.get(apiUrl, { responseType: 'blob' }).pipe(
      map((response: Blob) => {
        return response;
      }),
      catchError((error) => {
        console.error("Error fetching audio:", error);
        throw error;
      })
    );
  }

  updateExamTime(exam_id: number, last_start: Date, finish_by: Date): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/updatetime`
    let body = new URLSearchParams();
    body.set('exam_id', exam_id.toString());
    body.set('last_start', new Date(last_start).toISOString());
    body.set('finish_by', new Date(finish_by).toISOString());

    return this.httpClient.put<APIResponse>(apiUrl, body).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  getExamLastTouchedInfo(exam_id: number): Observable<APIResponse> {
    let apiUrl = `${environment.nodeUrl}/exam/lasttouched?exam_id=${encodeParam(exam_id)}`

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  getExamContent(exam_id: number): Observable<APIResponse> {
    let apiUrl = `${environment.nodeUrl}/exam/details/content?exam_id=${encodeParam(exam_id)}`

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  getExamPartDetails(exam_id: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/student/part/details?exam_id=${encodeParam(exam_id)}`

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  removeExamInstance(exam_uuid: string): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/instance/remove?exam_uuid=${encodeParam(exam_uuid)}`

    return this.httpClient.delete<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  resetExamPart(exam_uuid: string, part_position: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/instance/partialreset?exam_uuid=${encodeParam(exam_uuid)}&part_position=${encodeParam(part_position)}`

    return this.httpClient.put<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  changeCanResumeToFalse(exam_uuid: string): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/student/continue?exam_uuid=${encodeParam(exam_uuid)}`

    return this.httpClient.put<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  getLatestPartDetails(uuid: string): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/student/continue/details?uuid=${encodeParam(uuid)}`

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  removeExamPartInstance(exampartinstance_id: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/exam/instance/part`

    let body = new URLSearchParams();
    body.set('exampartinstance_id', exampartinstance_id.toString());

    return this.httpClient.delete<APIResponse>(apiUrl, {body: body}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }
}
